import React from 'react';
import { Card } from '@mui/material';
import styled from 'styled-components';
import ErrorIcon from '@mui/icons-material/Error';
import { useParams } from 'react-router-dom';
import { convertEntryYearMap, type PAYMENT_TYPE } from '../../const';
import {
  ApplicationFeeStatus,
  useGetPaymentRecordPayload,
} from '../../../graphql';
import {
  AssessmentGrid as RowDisplay,
  type RowData,
} from '../../../../Assessment';
import { Alert, Button, Loading } from '../../../../../components';

export type ApplicationFeePaymentProps = {
  paymentType: PAYMENT_TYPE;
  helperText?: string | Record<string, string>;
  underNote?: string;
};

export const ApplicationFeePayment: React.FunctionComponent<
  ApplicationFeePaymentProps
> = (props: ApplicationFeePaymentProps) => {
  const { paymentType, helperText, underNote } = props;
  const { applicationId } = useParams();

  const { paymentRecord, loading, error } = useGetPaymentRecordPayload(
    applicationId ?? '',
    paymentType,
  );

  const {
    applicationNo,
    entryYear,
    department,
    programme,
    studyMode,
    paymentDeadline,
    paymentStatus,
    redirectUrl,
    paymentRedirectUrlInfo,
  } = paymentRecord;

  const handleOpenPaymentPage = (): void => {
    const form = document.createElement('form');
    form.method = 'post';
    form.target = '_blank';

    // The inputs should be paymentRedirectUrlInfo.
    const inputs = paymentRedirectUrlInfo as unknown as Record<string, string>;

    for (const key in inputs) {
      if (Object.hasOwn(inputs, key)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = inputs[key];
        form.appendChild(input);
      }
    }

    form.action = redirectUrl;

    document.body.appendChild(form);
    form.submit();
  };

  const statusDisplay = new Map<ApplicationFeeStatus, string>([
    [ApplicationFeeStatus.WAIVED, 'Waived'],
    [ApplicationFeeStatus.PAID, 'Paid'],
    [ApplicationFeeStatus.NOT_PAID, 'Outstanding'],
    [ApplicationFeeStatus.PAST_DUE, 'Past Due'],
    [ApplicationFeeStatus.CANCELLED, 'Cancelled'],
    [ApplicationFeeStatus.SUSPENDED, 'Pending for System Update'],
    [ApplicationFeeStatus.NOT_AVAILABLE, 'Not Available'],
  ]);

  const displayDebitNote = ![
    ApplicationFeeStatus.SUSPENDED,
    ApplicationFeeStatus.WAIVED,
    ApplicationFeeStatus.NOT_AVAILABLE,
  ].includes(paymentStatus);
  const paymentStatusText = statusDisplay.get(paymentStatus) ?? '';

  const paymentDisplayData: RowData[] = [
    {
      label: 'Application No.',
      value: applicationNo,
    },
    {
      label: 'Admission Semester',
      value: convertEntryYearMap[entryYear] ?? entryYear,
    },
    {
      label: 'Department',
      value: department,
    },
    {
      label: 'Programme',
      value: programme,
    },
    {
      label: 'Study Mode',
      value: studyMode,
    },
    ...(paymentStatus === ApplicationFeeStatus.SUSPENDED
      ? []
      : [
          {
            label: 'Payment Deadline',
            value: paymentDeadline,
          },
        ]),
    {
      label: 'Payment Status',
      value: (
        <div className="flex flex-row align-center">
          <span>{paymentStatusText}</span>
          {displayDebitNote && (
            <Button
              onClick={handleOpenPaymentPage}
              variant="contained"
              size="small"
              sx={{
                borderRadius: '1rem',
                marginLeft: '1rem',
                fontSize: '0.8rem',
                lineHeight: '1rem',
              }}
            >
              {paymentStatus === ApplicationFeeStatus.NOT_PAID
                ? 'Go to pay'
                : 'Debit Note'}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const targetHelperText =
    typeof helperText === 'string' ? helperText : helperText?.[paymentStatus];

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + error?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const validHelperText = Boolean(targetHelperText);
  return (
    <div>
      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="w-full px-24">
            {validHelperText && (
              <div className="flex w-full mb-2 items-start">
                <HelperText className="flex !items-start mb-2 justify-start">
                  <ErrorIcon sx={{ color: '#951d1d' }} className="mr-2" />
                  {targetHelperText}
                </HelperText>
              </div>
            )}
            <Card
              sx={{
                boxShadow: 'lg',
                backgroundColor: '#f5f5f5',
                padding: '2rem 4rem',
                width: '100%',
              }}
            >
              <RowDisplay rows={paymentDisplayData} />
            </Card>
            {underNote !== undefined && (
              <div className="py-2 text-[#951d1d]">
                <b>Note:&nbsp;</b>
                <span
                  dangerouslySetInnerHTML={{
                    __html: underNote,
                  }}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const HelperText = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  p {
    margin-left: 0.5rem;
    font-size: 1rem;
  }
`;
